import { useQuery } from '@tanstack/react-query'
import { useFileAttachments } from '@features/shared/hooks/useFileAttachments'
import type { SupportAttachment, SupportAttachmentLimits } from '../api/contracts'
import { panelsQueryKeys } from '../api/queryKeys'
import {
  getSupportAttachmentLimits,
  removeSupportAttachment,
  uploadSupportAttachment,
} from '../api/support.api'

export type SupportAttachmentKind = SupportAttachment['kind']

export const SUPPORTED_SUPPORT_FILES_TEXT =
  'Attach screenshots or photos (JPEG, PNG, GIF, WebP) or PDFs.'
/** For the file picker. The server decides from a file's bytes, not this. */
export const SUPPORT_ATTACHMENT_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,application/pdf,.pdf'

const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp'])
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp'])

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase()
}

/** What a picked file looks like it is; null when it's neither an image nor a PDF. */
export function supportAttachmentKindOf(
  file: Pick<File, 'name' | 'type'>
): SupportAttachmentKind | null {
  const extension = extensionOf(file.name)
  if (IMAGE_TYPES.has(file.type) || IMAGE_EXTENSIONS.has(extension)) return 'image'
  if (file.type === 'application/pdf' || extension === 'pdf') return 'pdf'
  return null
}

function megabytes(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`
}

/** Why a file can't be attached to a request, or null when it may be uploaded. */
export function supportAttachmentProblem(
  file: Pick<File, 'name' | 'type' | 'size'>,
  limits: SupportAttachmentLimits
): string | null {
  const kind = supportAttachmentKindOf(file)
  if (kind === null) return SUPPORTED_SUPPORT_FILES_TEXT
  if (file.size === 0) return "It's empty."
  const max = kind === 'image' ? limits.image_max_bytes : limits.pdf_max_bytes
  if (file.size > max) {
    return `${kind === 'image' ? 'Images' : 'PDFs'} can be at most ${megabytes(max)}.`
  }
  return null
}

/**
 * The files on the support form: screenshots and PDFs for Arvoke staff, each
 * uploaded the moment it's picked, pasted or dropped. The server's limits say
 * whether attachments are on here at all (`SUPPORT_ATTACHMENTS_ENABLED`).
 */
export function useSupportAttachments() {
  const limitsQuery = useQuery({
    queryKey: panelsQueryKeys.supportAttachmentLimits,
    queryFn: getSupportAttachmentLimits,
    staleTime: Infinity,
  })
  const limits = limitsQuery.data
  const available = Boolean(limits?.enabled)

  const files = useFileAttachments<SupportAttachment, SupportAttachmentKind>({
    available,
    maxFiles: limits?.max_files_per_request,
    tooManyFilesMessage: limits
      ? `A request can carry at most ${limits.max_files_per_request} files.`
      : '',
    kindOf: (file) => supportAttachmentKindOf(file) ?? 'image',
    problemOf: (file) => (limits ? supportAttachmentProblem(file, limits) : null),
    upload: uploadSupportAttachment,
    remove: (attachment) => removeSupportAttachment(attachment.attachment_id),
    uploadedName: (attachment) => attachment.file_name,
    // Closing the panel clears its draft, files included.
    deleteUnsentOnUnmount: true,
  })
  return { available, limits, ...files }
}

export type SupportAttachments = ReturnType<typeof useSupportAttachments>
